import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { FaList, FaTh, FaInfoCircle } from "react-icons/fa";

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [roles, setRoles] = useState([]); // Thêm state để lưu danh sách vai trò
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [newUser, setNewUser] = useState({
    name: "",
    email: "",
    username: "",
    password: "",
    role: "user",
    dateOfBirth: new Date("2000-01-01").toISOString().split("T")[0],
  });
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [filterRole, setFilterRole] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [sortField, setSortField] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage] = useState(10);

  const [viewMode, setViewMode] = useState("list");
  const [successMessage, setSuccessMessage] = useState(null);

  const navigate = useNavigate();
  const { user, fetchWithRetry, isLoading, logout } = useAuth();

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      console.log("Token from localStorage:", localStorage.getItem("token"));
      const res = await fetchWithRetry({
        method: "get",
        url: "/api/admin/users",
      });
      console.log("Users response:", res.data);
      setUsers(res.data);
      setFilteredUsers(res.data);
      setLoading(false);
    } catch (error) {
      console.error("Lỗi tải danh sách người dùng:", error.response?.data || error);
      setError(
        error.response?.data?.message ||
        (error.response?.status === 403
          ? "Bạn không có quyền truy cập trang này! Vui lòng đăng nhập lại."
          : "Không thể tải danh sách người dùng!")
      );
      if (error.response?.status === 403 || error.response?.status === 401) {
        setTimeout(() => logout(), 3000);
      }
      setLoading(false);
    }
  }, [fetchWithRetry, logout]);

  const loadRoles = useCallback(async () => {
    try {
      const res = await fetchWithRetry({
        method: "get",
        url: "/api/admin/roles",
      });
      console.log("Roles response:", res.data);
      setRoles(res.data);
    } catch (error) {
      console.error("Lỗi tải danh sách vai trò:", error);
      setError(error.response?.data?.message || "Không thể tải danh sách vai trò!");
    }
  }, [fetchWithRetry]);

  const handleDelete = useCallback(
    async (id) => {
      if (!window.confirm("Bạn có chắc muốn xóa người dùng này?")) return;
      try {
        await fetchWithRetry({
          method: "delete",
          url: `/api/admin/users/${id}`,
        });
        setUsers((prevUsers) => prevUsers.filter((user) => user._id !== id));
        setFilteredUsers((prevUsers) => prevUsers.filter((user) => user._id !== id));
        setSuccessMessage("Xóa người dùng thành công!");
        setTimeout(() => setSuccessMessage(null), 3000);
      } catch (error) {
        console.error("Lỗi xóa người dùng:", error);
        setError(error.response?.data?.message || "Không thể xóa người dùng!");
      }
    },
    [fetchWithRetry]
  );

  const handleAddUser = useCallback(async () => {
    if (!newUser.name || !newUser.email || !newUser.password) {
      setError("Vui lòng nhập đầy đủ tên, email và mật khẩu!");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newUser.email)) {
      setError("Email không hợp lệ!");
      return;
    }

    if (newUser.password.length < 6) {
      setError("Mật khẩu phải có ít nhất 6 ký tự!");
      return;
    }

    try {
      console.log("Dữ liệu gửi đi:", newUser);
      const response = await fetchWithRetry({
        method: "post",
        url: "/api/admin/users",
        data: newUser,
      });
      console.log("Response from addUser:", response.data);
      setNewUser({
        name: "",
        email: "",
        username: "",
        password: "",
        role: "user",
        dateOfBirth: new Date("2000-01-01").toISOString().split("T")[0],
      });
      setShowAddModal(false);
      setSuccessMessage("Thêm người dùng thành công!");
      setTimeout(() => setSuccessMessage(null), 3000);
      await loadUsers();
    } catch (error) {
      console.error("Lỗi thêm người dùng:", error.response?.data || error);
      setError(
        error.response?.data?.message ||
        (error.response?.status === 400
          ? "Dữ liệu không hợp lệ! Vui lòng kiểm tra lại."
          : "Không thể thêm người dùng!")
      );
    }
  }, [newUser, loadUsers, fetchWithRetry]);

  const handleEditUser = useCallback(async () => {
    if (!editingUser.name) {
      setError("Tên không được để trống!");
      return;
    }

    if (editingUser.password && editingUser.password.length < 6) {
      setError("Mật khẩu mới phải có ít nhất 6 ký tự!");
      return;
    }

    try {
      console.log("Dữ liệu cập nhật người dùng:", editingUser);
      await fetchWithRetry({
        method: "put",
        url: `/api/admin/users/${editingUser._id}`,
        data: editingUser,
      });
      setSuccessMessage("Cập nhật thành công!");
      setTimeout(() => setSuccessMessage(null), 3000);
      setEditingUser(null);
      setShowEditModal(false);
      await loadUsers();
    } catch (error) {
      console.error("Lỗi cập nhật người dùng:", error);
      setSuccessMessage("Lỗi cập nhật!");
      setTimeout(() => setSuccessMessage(null), 3000);
      setError(error.response?.data?.message || "Không thể cập nhật người dùng!");
    }
  }, [editingUser, loadUsers, fetchWithRetry]);

  const toggleStatus = useCallback(
    async (id, currentStatus) => {
      try {
        const userToUpdate = users.find((user) => user._id === id);
        if (!userToUpdate) {
          setError("Không tìm thấy người dùng để cập nhật trạng thái!");
          return;
        }

        const updatedData = {
          ...userToUpdate,
          status: currentStatus === "active" ? "banned" : "active",
        };

        await fetchWithRetry({
          method: "put",
          url: `/api/admin/users/${id}`,
          data: updatedData,
        });
        setSuccessMessage(
          currentStatus === "active" ? "Khóa người dùng thành công!" : "Mở khóa thành công!"
        );
        setTimeout(() => setSuccessMessage(null), 3000);
        await loadUsers();
      } catch (error) {
        console.error("Lỗi cập nhật trạng thái người dùng:", error);
        setSuccessMessage("Lỗi cập nhật!");
        setTimeout(() => setSuccessMessage(null), 3000);
        setError(error.response?.data?.message || "Không thể cập nhật trạng thái người dùng!");
      }
    },
    [users, loadUsers, fetchWithRetry]
  );

  useEffect(() => {
    let filtered = [...users];

    if (searchTerm) {
      filtered = filtered.filter(
        (user) =>
          user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterRole) {
      filtered = filtered.filter((user) => user.role === filterRole);
    }

    if (filterStatus) {
      filtered = filtered.filter((user) => user.status === filterStatus);
    }

    filtered.sort((a, b) => {
      const fieldA = a[sortField]?.toString().toLowerCase() || "";
      const fieldB = b[sortField]?.toString().toLowerCase() || "";
      if (sortOrder === "asc") {
        return fieldA > fieldB ? 1 : -1;
      } else {
        return fieldA < fieldB ? 1 : -1;
      }
    });

    setFilteredUsers(filtered);
    setCurrentPage(1);
  }, [users, searchTerm, filterRole, filterStatus, sortField, sortOrder]);

  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  useEffect(() => {
    if (isLoading) return;

    console.log("Current user from useAuth:", user);
    if (
      !user ||
      (!user.permissions.includes("edit_users") && !user.permissions.includes("delete_users"))
    ) {
      console.log("User permission check failed:", user);
      setError("Bạn không có quyền truy cập trang này!");
      navigate("/login");
      return;
    }
    loadUsers();
    loadRoles(); // Tải danh sách vai trò
  }, [user, navigate, loadUsers, loadRoles, isLoading]);

  return (
    <div className="p-6 min-h-screen bg-gray-50">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-3xl font-bold text-gray-800">Quản lý người dùng</h2>
        <button
          onClick={() => setShowAddModal(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition duration-200"
          disabled={loading}
        >
          + Thêm người dùng
        </button>
      </div>

      {error && <p className="text-red-500 mb-4">{error}</p>}
      {successMessage && (
        <p
          className={`mb-4 ${
            successMessage.includes("thành công") ? "text-green-500" : "text-red-500"
          }`}
        >
          {successMessage}
        </p>
      )}

      <div className="flex flex-wrap gap-4 mb-6 items-center">
        <div className="flex-1 min-w-[200px]">
          <input
            type="text"
            placeholder="Tìm kiếm theo tên hoặc email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="min-w-[150px]">
          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
            className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Tất cả vai trò</option>
            {roles.map((role) => (
              <option key={role._id} value={role.role}>
                {role.role}
              </option>
            ))}
          </select>
        </div>
        <div className="min-w-[150px]">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Tất cả trạng thái</option>
            <option value="active">Active</option>
            <option value="banned">Banned</option>
          </select>
        </div>
        <div className="flex gap-2 min-w-[200px]">
          <select
            value={sortField}
            onChange={(e) => setSortField(e.target.value)}
            className="p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="name">Tên</option>
            <option value="email">Email</option>
            <option value="role">Vai trò</option>
            <option value="status">Trạng thái</option>
          </select>
          <select
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            className="p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="asc">Tăng dần</option>
            <option value="desc">Giảm dần</option>
          </select>
        </div>
        <div className="flex gap-2 ml-auto">
          <button
            onClick={() => setViewMode("list")}
            className={`p-2 rounded-lg ${
              viewMode === "list" ? "bg-blue-500 text-white" : "bg-gray-200 text-gray-700"
            } hover:bg-blue-400 transition duration-200`}
            title="Chế độ List"
          >
            <FaList />
          </button>
          <button
            onClick={() => setViewMode("gallery")}
            className={`p-2 rounded-lg ${
              viewMode === "gallery" ? "bg-blue-500 text-white" : "bg-gray-200 text-gray-700"
            } hover:bg-blue-400 transition duration-200`}
            title="Chế độ Gallery"
          >
            <FaTh />
          </button>
          <button
            onClick={() => setViewMode("detail")}
            className={`p-2 rounded-lg ${
              viewMode === "detail" ? "bg-blue-500 text-white" : "bg-gray-200 text-gray-700"
            } hover:bg-blue-400 transition duration-200`}
            title="Chế độ Detail"
          >
            <FaInfoCircle />
          </button>
        </div>
      </div>

      {loading ? (
        <p className="text-center text-gray-500">Đang tải...</p>
      ) : currentUsers.length === 0 ? (
        <p className="text-center text-gray-500">Không có người dùng nào để hiển thị.</p>
      ) : viewMode === "list" ? (
        <div className="overflow-x-auto">
          <table className="w-full bg-white rounded-lg shadow-lg">
            <thead>
              <tr className="bg-gradient-to-r from-blue-500 to-blue-600 text-white">
                <th className="p-4 text-left font-semibold">Tên</th>
                <th className="p-4 text-left font-semibold">Email</th>
                <th className="p-4 text-left font-semibold">Vai trò</th>
                <th className="p-4 text-left font-semibold">Trạng thái</th>
                <th className="p-4 text-left font-semibold">Hành động</th>
              </tr>
            </thead>
            <tbody>
              {currentUsers.map((user) => (
                <tr
                  key={user._id}
                  className="border-b hover:bg-gray-50 transition duration-200"
                >
                  <td className="p-4 text-gray-800">{user.name}</td>
                  <td className="p-4 text-gray-600">{user.email}</td>
                  <td className="p-4 capitalize text-gray-700">{user.role}</td>
                  <td
                    className={`p-4 capitalize ${
                      user.status === "active" ? "text-green-600" : "text-red-600"
                    }`}
                  >
                    {user.status}
                  </td>
                  <td className="p-4 space-x-2">
                    <button
                      onClick={() => toggleStatus(user._id, user.status)}
                      className={`px-3 py-1 rounded text-white ${
                        user.status === "active"
                          ? "bg-yellow-500 hover:bg-yellow-600"
                          : "bg-gray-500 hover:bg-gray-600"
                      } transition duration-200`}
                    >
                      {user.status === "active" ? "Khóa" : "Mở khóa"}
                    </button>
                    <button
                      onClick={() => {
                        setEditingUser({ ...user, password: "" });
                        setShowEditModal(true);
                      }}
                      className="px-3 py-1 rounded bg-blue-500 text-white hover:bg-blue-600 transition duration-200"
                    >
                      Sửa
                    </button>
                    <button
                      onClick={() => handleDelete(user._id)}
                      className="px-3 py-1 rounded bg-red-500 text-white hover:bg-red-600 transition duration-200"
                    >
                      Xóa
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : viewMode === "gallery" ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {currentUsers.map((user) => (
            <div
              key={user._id}
              className="bg-white p-4 rounded-lg shadow-lg hover:shadow-xl transition duration-200"
            >
              <h3 className="text-lg font-semibold text-gray-800">{user.name}</h3>
              <p className="text-gray-600">{user.email}</p>
              <p className="text-gray-600 capitalize">Vai trò: {user.role}</p>
              <p
                className={`text-gray-600 capitalize ${
                  user.status === "active" ? "text-green-600" : "text-red-600"
                }`}
              >
                Trạng thái: {user.status}
              </p>
              <div className="flex justify-between mt-4">
                <button
                  onClick={() => toggleStatus(user._id, user.status)}
                  className={`px-3 py-1 rounded text-white ${
                    user.status === "active"
                      ? "bg-yellow-500 hover:bg-yellow-600"
                      : "bg-gray-500 hover:bg-gray-600"
                  } transition duration-200`}
                >
                  {user.status === "active" ? "Khóa" : "Mở khóa"}
                </button>
                <button
                  onClick={() => {
                    setEditingUser({ ...user, password: "" });
                    setShowEditModal(true);
                  }}
                  className="px-3 py-1 rounded bg-blue-500 text-white hover:bg-blue-600 transition duration-200"
                >
                  Sửa
                </button>
                <button
                  onClick={() => handleDelete(user._id)}
                  className="px-3 py-1 rounded bg-red-500 text-white hover:bg-red-600 transition duration-200"
                >
                  Xóa
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {currentUsers.map((user) => (
            <div
              key={user._id}
              className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition duration-200"
            >
              <h3 className="text-xl font-semibold text-gray-800 mb-2">{user.name}</h3>
              <div className="space-y-2">
                <p className="text-gray-600">
                  <span className="font-medium">Email:</span> {user.email}
                </p>
                <p className="text-gray-600">
                  <span className="font-medium">Thời gian khóa:</span>{" "}
                  {user.bannedAt
                    ? new Date(user.bannedAt).toLocaleString("vi-VN")
                    : "Chưa bị khóa"}
                </p>
                <p className="text-gray-600 capitalize">
                  <span className="font-medium">Vai trò:</span> {user.role}
                </p>
                <p
                  className={`text-gray-600 capitalize ${
                    user.status === "active" ? "text-green-600" : "text-red-600"
                  }`}
                >
                  <span className="font-medium">Trạng thái:</span> {user.status}
                </p>
                <p className="text-gray-600">
                  <span className="font-medium">Ngày sinh:</span>{" "}
                  {user.dateOfBirth
                    ? new Date(user.dateOfBirth).toLocaleDateString("vi-VN")
                    : "Chưa cập nhật"}
                </p>
                <p className="text-gray-600 capitalize">
                  <span className="font-medium">Giới tính:</span> {user.gender || "Chưa cập nhật"}
                </p>
                <p className="text-gray-600">
                  <span className="font-medium">Tiểu sử:</span> {user.bio || "Chưa cập nhật"}
                </p>
                <p className="text-gray-600">
                  <span className="font-medium">Địa chỉ:</span>{" "}
                  {user.locationText?.city || user.locationText?.country
                    ? `${user.locationText.city || ""}, ${user.locationText.country || ""}`
                    : "Chưa cập nhật"}
                </p>
              </div>
              <div className="flex justify-between mt-4">
                <button
                  onClick={() => toggleStatus(user._id, user.status)}
                  className={`px-3 py-1 rounded text-white ${
                    user.status === "active"
                      ? "bg-yellow-500 hover:bg-yellow-600"
                      : "bg-gray-500 hover:bg-gray-600"
                  } transition duration-200`}
                >
                  {user.status === "active" ? "Khóa" : "Mở khóa"}
                </button>
                <button
                  onClick={() => {
                    setEditingUser({ ...user, password: "" });
                    setShowEditModal(true);
                  }}
                  className="px-3 py-1 rounded bg-blue-500 text-white hover:bg-blue-600 transition duration-200"
                >
                  Sửa
                </button>
                <button
                  onClick={() => handleDelete(user._id)}
                  className="px-3 py-1 rounded bg-red-500 text-white hover:bg-red-600 transition duration-200"
                >
                  Xóa
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex justify-center mt-6">
          <nav className="inline-flex rounded-md shadow">
            <button
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-1 rounded-l-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-100 disabled:opacity-50"
            >
              Trước
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => paginate(page)}
                className={`px-3 py-1 border border-gray-300 ${
                  currentPage === page
                    ? "bg-blue-500 text-white"
                    : "bg-white text-gray-500 hover:bg-gray-100"
                }`}
              >
                {page}
              </button>
            ))}
            <button
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-1 rounded-r-md border border-gray-300 bg-white text-gray-500 hover:bg-gray-100 disabled:opacity-50"
            >
              Sau
            </button>
          </nav>
        </div>
      )}

      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md">
            <h3 className="text-xl font-semibold mb-4">Thêm người dùng mới</h3>
            <input
              type="text"
              placeholder="Tên"
              value={newUser.name}
              onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
              className="w-full p-2 mb-3 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <input
              type="email"
              placeholder="Email"
              value={newUser.email}
              onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
              className="w-full p-2 mb-3 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <input
              type="text"
              placeholder="Username"
              value={newUser.username}
              onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
              className="w-full p-2 mb-3 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <input
              type="password"
              placeholder="Mật khẩu"
              value={newUser.password}
              onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
              className="w-full p-2 mb-3 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <select
              value={newUser.role}
              onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
              className="w-full p-2 mb-3 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {roles.map((role) => (
                <option key={role._id} value={role.role}>
                  {role.role}
                </option>
              ))}
            </select>
            <input
              type="date"
              value={newUser.dateOfBirth}
              onChange={(e) => setNewUser({ ...newUser, dateOfBirth: e.target.value })}
              className="w-full p-2 mb-3 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowAddModal(false)}
                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition duration-200"
              >
                Hủy
              </button>
              <button
                onClick={handleAddUser}
                className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 transition duration-200"
              >
                Thêm
              </button>
            </div>
          </div>
        </div>
      )}

      {showEditModal && editingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md">
            <h3 className="text-xl font-semibold mb-4">Chỉnh sửa người dùng</h3>
            <input
              type="text"
              placeholder="Tên"
              value={editingUser.name}
              onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })}
              className="w-full p-2 mb-3 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <select
              value={editingUser.role}
              onChange={(e) => setEditingUser({ ...editingUser, role: e.target.value })}
              className="w-full p-2 mb-3 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {roles.map((role) => (
                <option key={role._id} value={role.role}>
                  {role.role}
                </option>
              ))}
            </select>
            <input
              type="password"
              placeholder="Mật khẩu mới (để trống nếu không đổi)"
              value={editingUser.password || ""}
              onChange={(e) => setEditingUser({ ...editingUser, password: e.target.value })}
              className="w-full p-2 mb-3 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setEditingUser(null);
                  setShowEditModal(false);
                }}
                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition duration-200"
              >
                Hủy
              </button>
              <button
                onClick={handleEditUser}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition duration-200"
              >
                Cập nhật
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;